/**
 * Exchange price primitives — the back/lay cell and the 3+3 ladder.
 *
 * Both are APPROVED entries in the W7 inventory (`OddsCell`, `OddsLadder` in
 * specs/design-system/w7/tokens.yaml) that had simply never been built, so this
 * is implementing the design system rather than extending it. The four tokens
 * they consume — --w7-back-bg/-text/-volume and the lay trio — have existed
 * since v0.1.0 and were referenced by nothing but two chip rules.
 *
 * Prices arrive as strings. The kit never formats a price for the same reason
 * it never formats a rupee: `odds()` in the app decides that 1.9 prints as
 * "1.90", and two answers to that question is one too many.
 */

import type { ReactNode } from 'react'
import { cx } from './core'

/* ------------------------------------------------------------- odds cell -- */

export interface OddsCellProps {
  side: 'back' | 'lay'
  /** Pre-formatted, e.g. "2.34". Always two decimals so 1.9 and 1.90 cannot differ in width. */
  price: string
  /** Pre-formatted volume at that price, e.g. "₹63.4k". */
  size?: string
  /** 0 = best price. Deeper levels render faded, per the showcase's ladder rule. */
  depth?: number
  state?: 'rest' | 'suspended'
  /**
   * Player app only — the console NEVER passes this. Supplying it turns the
   * cell into a button, and a price an operator can press is a price they will
   * believe they pressed. This console is explicitly not a betting terminal.
   */
  onClick?: () => void
  /** Required whenever `onClick` is set: an icon-sized control needs a name. */
  label?: string
  className?: string
}

export function OddsCell({
  side,
  price,
  size,
  depth = 0,
  state = 'rest',
  onClick,
  label,
  className,
}: OddsCellProps) {
  const body = (
    <>
      <span className="w7-odds-price">{price}</span>
      {size ? <span className="w7-odds-size">{size}</span> : null}
    </>
  )
  const attrs = {
    className: cx('w7-odds', className),
    'data-side': side,
    'data-depth': depth,
    'data-state': state === 'suspended' ? 'suspended' : undefined,
  } as const

  // A span, not a disabled button: a disabled control is still announced as a
  // control, and there is nothing here to activate.
  if (!onClick) return <span {...attrs}>{body}</span>
  return (
    <button type="button" aria-label={label} {...attrs} onClick={onClick}>
      {body}
    </button>
  )
}

/* ----------------------------------------------------------- odds ladder -- */

export interface OddsLadderLevel {
  price: string
  size?: string
}

export interface OddsLadderRow {
  key: string
  /** Left cell. The caller supplies the runner name and its already-formatted position. */
  runner: ReactNode
  /** Outermost first, BEST last — the order the price columns are read in. */
  back: OddsLadderLevel[]
  /** BEST first, outermost last. */
  lay: OddsLadderLevel[]
  suspended?: boolean
}

export interface OddsLadderProps {
  rows: OddsLadderRow[]
  /** Caption, e.g. "Sri Lanka W v Pakistan W — Match Odds prices". */
  label: string
  /** Levels per side. 3 on desktop; CSS collapses to 1 below 720px. */
  depth?: number
  /** Replaces the price columns when the market has no book at all. */
  emptyMessage?: ReactNode
}

/**
 * One row per runner, three back columns then three lay columns.
 *
 * A real `<table>` rather than a row of flexboxes, because the whole point is
 * that the price columns line up ACROSS runners — the one thing the product
 * being replaced got right, and the thing a per-row flexbox loses the moment
 * one runner's price is four characters and another's is three.
 *
 * Below 720px the outer levels are hidden by CSS rather than sliced here, so
 * the markup is identical at every width and there is no resize listener to get
 * wrong. Three levels of depth on a phone is noise: the operator on a phone is
 * checking the position, not the ladder.
 */
export function OddsLadder({ rows, label, depth = 3, emptyMessage }: OddsLadderProps) {
  return (
    <table className="w7-oddsladder">
      <caption className="w7-sr">{label}</caption>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} data-suspended={row.suspended ? 'true' : undefined}>
            <th scope="row" className="w7-oddsladder-runner">
              {row.runner}
            </th>
            {row.suspended ? (
              <td className="w7-oddsladder-susp" colSpan={depth * 2}>
                Suspended
              </td>
            ) : (
              <>
                {/* `back` is outermost-first, so the BEST price is the last of the
                    slice — depth counts backwards from the end. */}
                {padLevels(row.back, depth, 'end').map((level, i) => (
                  <td key={`back-${i}`} data-depth={depth - 1 - i}>
                    {level ? <OddsCell side="back" price={level.price} size={level.size} depth={depth - 1 - i} /> : null}
                  </td>
                ))}
                {padLevels(row.lay, depth, 'start').map((level, i) => (
                  <td key={`lay-${i}`} data-depth={i}>
                    {level ? <OddsCell side="lay" price={level.price} size={level.size} depth={i} /> : null}
                  </td>
                ))}
              </>
            )}
          </tr>
        ))}
        {rows.length === 0 && emptyMessage ? (
          <tr>
            <td className="w7-oddsladder-empty" colSpan={depth * 2 + 1}>
              {emptyMessage}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  )
}

/**
 * Fit a side's levels to exactly `depth` cells, keeping the BEST price against
 * the middle of the ladder. A market quoting two levels must leave the OUTER
 * column blank, never the inner one, or the two runners' best prices stop
 * sharing a column and the ladder's only job is lost.
 */
function padLevels(
  levels: OddsLadderLevel[],
  depth: number,
  keep: 'start' | 'end',
): (OddsLadderLevel | null)[] {
  const out: (OddsLadderLevel | null)[] = Array.from({ length: depth }, () => null)
  if (keep === 'end') {
    const slice = levels.slice(-depth)
    for (let i = 0; i < slice.length; i++) out[depth - slice.length + i] = slice[i] ?? null
  } else {
    const slice = levels.slice(0, depth)
    for (let i = 0; i < slice.length; i++) out[i] = slice[i] ?? null
  }
  return out
}
